import React, { useEffect, useState } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Table, Card, Row, Col, Badge, Form } from "react-bootstrap";
import { FiServer, FiCheck, FiGlobe, FiUsers, FiLink, FiInfo } from "react-icons/fi";
import { Spinner } from "react-bootstrap";
import CopyIcon from "../components/CopyIcon";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-cluster/lib/assets/MarkerCluster.css';
import 'react-leaflet-cluster/lib/assets/MarkerCluster.Default.css';
import L from "leaflet";
import Graph from "react-graph-vis";
import { PiscanAPI } from "../api/piscan";
import { CardContent } from "../components/Card";
// import AdUnit from "../components/AdUnit";
import { useLanguage } from "../context/LanguageContext";
import AdSenseUnit from "../components/AdsenseUnit";
import AdUnit from "../components/AdUnit";

// Core Team addresses that we know from previous data
const CORE_TEAM_IPS = ["34.47.29.105", "35.228.152.141", "34.88.93.19"];

// Define types for the data we'll receive
interface GeoData {
  city: string;
  country: string;
  country_code: string;
  isp: string;
  lat?: number;
  lon?: number;
  org: string;
  region: string;
}

interface NodeDetail {
  address: string;
  connected: boolean;
  geo?: GeoData;
  is_validator?: boolean;
  ledger_version?: number;
  listening_port?: number;
  overlay_min_ver?: number;
  overlay_version?: number;
  version?: string;
}

interface QuorumSet {
  inner_sets: any[];
  threshold: number;
  validators: string[];
}

interface NodeMapData {
  active_connections: number;
  hash_to_nodes: Record<string, string[]>;
  hash_to_validators: Record<string, string[]>;
  known_peers: Record<string, boolean>;
  known_peers_count: number;
  node_details: Record<string, NodeDetail>;
  node_to_address: Record<string, string>;
  node_to_quorum_set: Record<string, QuorumSet>;
  quorum_set_status: Record<string, boolean>;
  quorum_sets_found: number;
  quorum_sets_total: number;
  timestamp: string;
}

const Nodes: React.FC = () => {
  const [nodeMapData, setNodeMapData] = useState<NodeMapData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<{ nodes: any[], links: any[] }>({ nodes: [], links: [] });
  const [highlightValidators, setHighlightValidators] = useState<boolean>(true);
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const { t, language } = useLanguage(); // Add language hook
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const [graphHighlightedNode, setGraphHighlightedNode] = useState<string | null>(null);

  useEffect(() => {
    setHeading(t('nodes.heading'));
    setTitle(t('nodes.title'));
    setDescription(t('nodes.description'));
  }, [setHeading, setTitle, setDescription, t, language]);
  
  useEffect(() => {
    fetchNodeMap();
  }, []);

  // Process nodes data into graph format when data is loaded
  useEffect(() => {
    if (nodeMapData) {
      processGraphData();
    }
  }, [nodeMapData]);

  // Make sure we have a custom icon for Leaflet
  useEffect(() => {
    // Fix Leaflet icon issue
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    });
  }, []);

  const fetchNodeMap = async () => {
    try {
      setLoading(true);
      const response = await fetch(PiscanAPI.NodeMap);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      setNodeMapData(data);
      setLoading(false);
    } catch (err) {
      setError(`Failed to fetch node map data: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  };

  // Update the processGraphData function to include inaccessible nodes

const processGraphData = () => {
  if (!nodeMapData) return;

  const nodes: any[] = [];
  const links: any[] = [];
  const nodeMap = new Map(); // Use a map to prevent duplicate nodes with the same ID
  const allNodeIds = new Set<string>();
  
  // Step 1: Collect all node IDs from various sources
  Object.keys(nodeMapData.node_details || {}).forEach(key => {
    if (key.startsWith('G')) {
      allNodeIds.add(key);
    }
  });
  
  Object.values(nodeMapData.node_to_quorum_set || {}).forEach(quorumSet => {
    if (quorumSet.validators) {
      quorumSet.validators.forEach(validator => {
        allNodeIds.add(validator);
      });
    }
  });
  
  // Create nodes for each entry in allNodeIds
  allNodeIds.forEach(nodeId => {
    // Skip if we've already processed this node
    if (nodeMap.has(nodeId)) return;
    
    // Get node details if available
    const details = nodeMapData.node_details[nodeId];
    // Get address from node_to_address or from details
    const address = nodeMapData.node_to_address[nodeId] || 
                    (details?.address || 'Unknown');
    
    const isAccessible = !!(details && details.connected);
    const isCoreTeam = address ? CORE_TEAM_IPS.some(ip => address.includes(ip)) : false;
    const isValidator = !!details?.is_validator;
    
    const nodeData = {
      id: nodeId,
      address: address,
      is_validator: isValidator,
      version: details?.version || 'Unknown',
      is_core_team: isCoreTeam,
      is_accessible: isAccessible,
      connected: details?.connected || false,
      geo: details?.geo,
      val: isAccessible ? (isValidator ? 30 : 15) : 10,
      color: isAccessible ? 
        (isValidator ? (isCoreTeam ? '#ff9900' : '#1a85ff') : (isCoreTeam ? '#ffd699' : '#a0d0ff'))
        : '#cccccc'
    };
    
    nodes.push(nodeData);
    nodeMap.set(nodeId, nodeData);
  });

    // Then, add connections ensuring all referenced nodes exist
    const edgeMap = new Map(); // To prevent duplicate edges
    
    Object.keys(nodeMapData.node_to_quorum_set || {}).forEach(nodeId => {
      const quorumSet = nodeMapData.node_to_quorum_set[nodeId];
      if (!quorumSet || !quorumSet.validators) return;
      
      // Skip if source node doesn't exist in our nodes array
      if (!nodeMap.has(nodeId)) return;
      
      quorumSet.validators.forEach(validator => {
        // Skip self-connections and connections to non-existent nodes
        if (nodeId === validator || !nodeMap.has(validator)) return;
        
        // Create a unique key for this edge to prevent duplicates
        const edgeKey = `${nodeId}->${validator}`;
        if (!edgeMap.has(edgeKey)) {
          links.push({
            source: nodeId,
            target: validator,
            value: 1
          });
          edgeMap.set(edgeKey, true);
        }
      });
    });

    console.log(`Created graph with ${nodes.length} nodes (${nodes.filter(n => !n.is_accessible).length} inaccessible) and ${links.length} connections`);
    setGraphData({ nodes, links });
  };

  const isCoreTeamNode = (address: string): boolean => {
    return CORE_TEAM_IPS.some(ip => address.includes(ip));
  };
  
  // Update the renderNodeTooltip function to return a simpler format for better compatibility
  const renderNodeTooltip = (node: any) => {
    const nodeDetail = nodeMapData?.node_details[node.id];
    // Create a simpler tooltip that works well with vis.js
    return `
      ${t('nodes.tooltip.node_id')}: ${node.id}
      ${t('nodes.tooltip.address')}: ${node.address !== 'Unknown' ? node.address : t('nodes.tooltip.not_accessible')}
      ${t('nodes.tooltip.type')}: ${node.is_validator ? t('nodes.validator') : t('nodes.watcher')}${node.is_core_team ? ` (${t('nodes.core_team')})` : ''}
      ${t('nodes.tooltip.status')}: ${node.connected ? t('nodes.status.connected') : (nodeDetail?.version ? t('nodes.status.disconnected') : t('nodes.status.private'))}
      ${nodeDetail?.version ? `${t('nodes.tooltip.version')}: ${nodeDetail.version}` : ''}
      ${node.geo && node.geo.country ? 
      `${t('nodes.tooltip.location')}: ${node.geo.city ? node.geo.city + ', ' : ''}${node.geo.country}` : ''}
    `;
  };
  
  const getValidatorCount = (): number => {
    if (!nodeMapData) return 0;
    
    let validatorCount = 0;
    Object.values(nodeMapData.node_details).forEach(details => {
      if (details.is_validator) {
        validatorCount++;
      }
    });
    
    return validatorCount;
  };

  // First, let's add a function to calculate how many nodes trust each node
  // You can add this function before the return statement
  const calculateTrustedByCounts = (): Record<string, number> => {
    if (!nodeMapData) return {};
    
    const trustedByCounts: Record<string, number> = {};
    
    // Initialize counts for all node IDs
    Object.keys(nodeMapData.node_details || {}).forEach(nodeId => {
      if (nodeId.startsWith('G')) {
        trustedByCounts[nodeId] = 0;
      }
    });
    
    // Count how many nodes have each node in their quorum set
    Object.entries(nodeMapData.node_to_quorum_set || {}).forEach(([nodeId, quorumSet]) => {
      if (quorumSet && quorumSet.validators) {
        quorumSet.validators.forEach(validator => {
          if (trustedByCounts[validator] !== undefined) {
            trustedByCounts[validator]++;
          } else {
            trustedByCounts[validator] = 1;
          }
        });
      }
    });
    
    return trustedByCounts;
  };

  // Add this function to calculate the number of nodes per country

const calculateNodesPerCountry = (): Record<string, number> => {
  if (!nodeMapData) return {};
  
  const nodesPerCountry: Record<string, number> = {};
  
  Object.values(nodeMapData.node_details || {}).forEach(details => {
    if (details.geo && details.geo.country) {
      const country = details.geo.country;
      if (nodesPerCountry[country]) {
        nodesPerCountry[country]++;
      } else {
        nodesPerCountry[country] = 1;
      }
    }
  });
  
  return nodesPerCountry;
};

// Add a new section to display the country statistics
const countryStats = calculateNodesPerCountry();

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div className="nodes-page">
      {/* Network Statistics Overview */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-4 mb-md-0">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-primary bg-opacity-10 me-3">
                {FiServer({size:24})}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('nodes.active_nodes')}</h6>
                <h3 className="mb-0 stats-number">{Object.keys(nodeMapData?.node_to_quorum_set || {}).length}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} sm={6} className="mb-4 mb-md-0">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-success bg-opacity-10 me-3">
                {FiCheck({size:24,className:"text-success"})}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('nodes.validators')}</h6>
                <h3 className="mb-0 stats-number">{getValidatorCount()}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} sm={6} className="mb-4 mb-md-0">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-info bg-opacity-10 me-3">
                {FiGlobe({size:24,className:"text-info"})}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('nodes.known_peers')}</h6>
                <h3 className="mb-0 stats-number">{nodeMapData?.known_peers_count || 0}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} sm={6}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              
              <div className="rounded-circle p-3 bg-danger bg-opacity-10 me-3">
                {FiLink({size:24,className:"text-danger"})}
              </div>
              <div>
                <h6 className="text-muted mb-1">{t('nodes.disconnected_nodes')}</h6>
                <h3 className="mb-0 stats-number">{nodeMapData?nodeMapData?.known_peers_count - Object.keys(nodeMapData?.node_to_quorum_set || {}).length:0}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Mobile-only Ad Card
      <div className="col-12 d-md-none" style={{marginTop: "20px",marginBottom: "20px"}}>
          <Card>
            <CardContent>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="card-title m-0">Sponsored</h5>
                <span className="badge bg-secondary text-white">Ad</span>
              </div>
              <AdUnit
                adId="67cca9c1aa72d3d47fe1425c"
                width="100%"
                height="100px"
                className="mt-2 mb-2 nodes-ad"
                adPosition="nodes"
                alternativeElement={
                  <div className="p-3 text-center bg-light">
                    <span className="text-muted">{t('common.ad_not_available')}</span>
                  </div>
                }
              />
            </CardContent>
          </Card>
        </div>
         */}
      <Row className="mb-4">
        <Col md={6}>
          {/* Node Network Graph */}
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h2 className="card-title">{t('nodes.network_graph')}</h2>
              <div>
                {/* <Form.Check
                  type="switch"
                  id="highlight-validators"
                  label="Highlight Validators"
                  checked={highlightValidators}
                  onChange={(e) => setHighlightValidators(e.target.checked)}
                  className="d-inline-block me-3"
                />
                <Badge bg="info">
                  {graphData.nodes.length} nodes / {graphData.links.length} connections
                </Badge> */}
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <div 
                id="graph-container"
                style={{ 
                  height: "500px", 
                  width: "100%", 
                  display: "flex", 
                  justifyContent: "center", 
                  alignItems: "center"
                }}
                onWheel={(e) => {
                  if (!e.ctrlKey && !e.metaKey) {
                    e.stopPropagation();
                  }
                }}
              >
                {graphData.nodes.length > 0 && (
                  <Graph
                    graph={{
                      nodes: graphData.nodes.map(node => ({
                        id: node.id,
                        label: node.id.substring(0, 4),
                        title: renderNodeTooltip(node),
                        shape: "dot",
                        size: node.is_validator ? 20 : 15,
                        color: {
                          background: node.color,
                          border: node.is_validator ? '#000000' : '#666666',
                          highlight: {
                            background: '#ff9900',
                            border: '#000000'
                          }
                        },
                        borderWidth: node.is_accessible ? 1 : 2,
                        borderDashes: !node.is_accessible,
                        font: {
                          size: 12,
                          color: node.is_accessible ? 
                            (node.is_validator ? '#000000' : '#444444') :
                            '#999999',
                        }
                      })),
                      // Update the edges mapping in the Graph component
edges: graphData.links.map((link, index) => ({
  id: `edge-${index}`, // Ensure each edge has a unique ID
  from: typeof link.source === 'object' ? link.source.id : link.source,
  to: typeof link.target === 'object' ? link.target.id : link.target,
  width: 1,
  length: 150,
  arrows: {
    to: {
      enabled: false
    }
  },
  color: {
    color: '#666666',
    highlight: '#ff9900'
  },
  smooth: false
}))
                    }}
                    options={{
                      nodes: {
                        borderWidth: 1,
                        shadow: false,
                        font: {
                          face: 'Arial',
                          size: 14
                        },
                        // Add tooltip configuration
                        tooltip: {
                          delay: 200,
                          fontColor: "black",
                          fontSize: 14,
                          fontFace: "arial",
                          color: {
                            border: "#666",
                            background: "#fff"
                          }
                        }
                      },
                      edges: {
                        width: 1,
                        smooth: {
                          type: 'continuous',
                          forceDirection: 'none'
                        },
                        color: '#666666'
                      },
                      // Other options remain the same
                      interaction: {
                        dragNodes: true,
                        dragView: true,
                        zoomView: true,
                        hover: true,
                        navigationButtons: false,
                        keyboard: {
                          enabled: false
                        },
                        tooltipDelay: 0, // Set to 0 for immediate tooltip display
                        hideEdgesOnDrag: false,
                        hideEdgesOnZoom: false
                      },
                      // Rest of the options remain the same
                      physics: {
                        stabilization: {
                          enabled: true,
                          iterations: 1000
                        },
                        barnesHut: {
                          gravitationalConstant: -2000,
                          centralGravity: 0.3,
                          springLength: 150,
                          springConstant: 0.05,
                          damping: 0.09
                        },
                        solver: 'barnesHut'
                      },
                      layout: {
                        improvedLayout: true,
                        hierarchical: {
                          enabled: false,
                          direction: 'UD',
                          sortMethod: 'directed'
                        }
                      },
                      // interaction: {
                      //   dragNodes: true,
                      //   dragView: true,
                      //   zoomView: true,
                      //   hover: true,
                      //   navigationButtons: false,
                      //   keyboard: {
                      //     enabled: false
                      //   },
                      //   tooltipDelay: 200
                      // },
                      groups: {
                        validator: {
                          shape: 'dot',
                          color: {
                            background: '#1a85ff',
                            border: '#000000'
                          }
                        },
                        coreValidator: {
                          shape: 'dot',
                          color: {
                            background: '#ff9900',
                            border: '#000000'
                          }
                        },
                        watcher: {
                          shape: 'dot',
                          color: {
                            background: '#a0d0ff',
                            border: '#666666'
                          }
                        },
                        coreWatcher: {
                          shape: 'dot',
                          color: {
                            background: '#ffd699',
                            border: '#666666'
                          }
                        },
                        disconnected: {
                          shape: 'dot',
                          color: {
                            background: '#cccccc',
                            border: '#666666'
                          },
                          borderDashes: true
                        }
                      }
                    }}
                    events={{
                      select: (event: { nodes: any; }) => {
                        const { nodes } = event;
                        if (nodes.length > 0) {
                          const nodeId = nodes[0];
                          if (graphHighlightedNode === nodeId) {
                            setGraphHighlightedNode(null);
                          } else {
                            setGraphHighlightedNode(nodeId);
                          }
                        } else {
                          setGraphHighlightedNode(null);
                        }
                      },
                      hoverNode: (event: any) => {
                        const graphContainer = document.getElementById('graph-container');
                        if (graphContainer && graphContainer.style) {
                          graphContainer.style.cursor = 'pointer';
                        }
                      },
                      blurNode: (event: any) => {
                        const graphContainer = document.getElementById('graph-container');
                        if (graphContainer && graphContainer.style) {
                          graphContainer.style.cursor = 'default';
                        }
                      },
                      stabilized: () => {
                        console.log("Graph stabilized");
                      }
                    }}
                    getNetwork={(network: any) => {
                      // Store reference to network if needed
                      // For example: setNetworkInstance(network)
                    }}
                  />
                )}
              </div>
              
              {/* Legend moved to bottom of card */}
              <div className="mt-4 pt-2 border-top">
                <h6 className="mb-2">{t('nodes.node_types')}</h6>
                <div className="d-flex flex-wrap">
                  <div className="me-4 mb-2 d-flex align-items-center">
                    <span className="d-inline-block me-2" style={{ width: '12px', height: '12px', backgroundColor: 'rgba(255, 153, 0, 1)', borderRadius: '50%' }}></span>
                    <span className="text-muted">{t('nodes.validator')}</span>
                  </div>
                  <div className="me-4 mb-2 d-flex align-items-center">
                    <span className="d-inline-block me-2" style={{ width: '12px', height: '12px', backgroundColor: '#a0d0ff', borderRadius: '50%' }}></span>
                    <span className="text-muted">{t('nodes.watcher')}</span>
                  </div>
                  <div className="me-4 mb-2 d-flex align-items-center">
                    <span className="d-inline-block me-2" style={{ width: '12px', height: '12px', backgroundColor: '#cccccc', borderRadius: '50%' }}></span>
                    <span className="text-muted">{t('nodes.disconnected_private')}</span>
                  </div>
                </div>
                <div className="mt-2">
                  <small className="text-muted">
                    {t('nodes.click_to_highlight')}
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          {/* Geographic Node Distribution Map */}
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h2 className="card-title">{t('nodes.global_distribution')}</h2>
              <Badge bg="info">
                {Object.values(nodeMapData?.node_details || {}).filter(node => node.geo?.lat && node.geo?.lon).length} {t('nodes.mapped_nodes')}
              </Badge>
            </Card.Header>
            <Card.Body>
              <div style={{ height: "500px", width: "100%" }}>
                <MapContainer 
                  center={[20, 0]} 
                  zoom={2} 
                  style={{ height: '100%', width: '100%' }}
                  minZoom={2}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <MarkerClusterGroup chunkedLoading>
                    {Object.entries(nodeMapData?.node_details || {})
                      .filter(([_, details]) => details.geo?.lat && details.geo?.lon) // Only include nodes with geo coordinates
                      .map(([nodeIdOrAddress, details]) => {
                        const isNodeId = nodeIdOrAddress.startsWith('G');
                        const isConnected = details.connected;
                        const isValidator = details.is_validator;
                        const isCoreTeam = CORE_TEAM_IPS.some(ip => details.address.includes(ip));
                        
                        // Get color based on node type - same as in graph
                        let nodeColor = isConnected ? 
                          (isValidator ? (isCoreTeam ? '#ff9900' : '#1a85ff') : (isCoreTeam ? '#ffd699' : '#a0d0ff'))
                          : '#cccccc';
                          
                        return (
                          <CircleMarker 
                            key={nodeIdOrAddress}
                            center={[details.geo!.lat!, details.geo!.lon!]}
                            radius={isValidator ? 8 : 5}
                            fillColor={nodeColor}
                            color="#000"
                            weight={1}
                            opacity={1}
                            fillOpacity={0.8}
                          >
                            <Popup>
                              <div>
                                <strong>{t('nodes.node_id')}:</strong> {isNodeId ? `${nodeIdOrAddress.substr(0, 6)}...${nodeIdOrAddress.substr(-6)}` : t('nodes.unknown')}
                                <br />
                                <strong>{t('nodes.address')}:</strong> {details.address}
                                <br />
                                <strong>{t('nodes.type')}:</strong> {isValidator ? t('nodes.validator') : t('nodes.watcher')} {isCoreTeam ? `(${t('nodes.core_team')})` : ''}
                                <br />
                                <strong>{t('nodes.status')}:</strong> {isConnected ? t('nodes.status.connected') : (details.version ? t('nodes.status.private') : t('nodes.status.disconnected'))}
                                <br />
                                <strong>{t('nodes.location')}:</strong> {details.geo?.city ? `${details.geo.city}, ` : ''}{details.geo?.country}
                                {details.version && (<><br /><strong>{t('nodes.version')}:</strong> {details.version.replace('stellar-core ', '').split(' ')[0]}</>)}
                              </div>
                            </Popup>
                          </CircleMarker>
                        );
                      })}
                  </MarkerClusterGroup>
                </MapContainer>
              </div>
              <div className="mt-3">
                <div className="d-flex flex-wrap">
                  <div className="me-4 mb-2 d-flex align-items-center">
                    <span className="d-inline-block me-2" style={{ width: '12px', height: '12px', backgroundColor: '#1a85ff', borderRadius: '50%' }}></span>
                    <span className="text-muted">{t('nodes.validator')}</span>
                  </div>
                  <div className="me-4 mb-2 d-flex align-items-center">
                    <span className="d-inline-block me-2" style={{ width: '12px', height: '12px', backgroundColor: '#a0d0ff', borderRadius: '50%' }}></span>
                    <span className="text-muted">{t('nodes.watcher')}</span>
                  </div>
                  <div className="me-4 mb-2 d-flex align-items-center">
                    <span className="d-inline-block me-2" style={{ width: '12px', height: '12px', backgroundColor: '#cccccc', borderRadius: '50%' }}></span>
                    <span className="text-muted">{t('nodes.disconnected_private')}</span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <div className="row g-3 mb-4  d-block d-md-none">
        {/* <AdSenseUnit/> */}
        <AdUnit
                  className="67cca9c1aa72d3d47fe1425c"
                  width="300px"
                  height="100px"
                  centered={true}
                />
      </div>
      {/* Nodes Table with Details - Single flat list with priority sorting */}
<Card>
  <Card.Header className="d-flex justify-content-between align-items-center">
    <h2 className="card-title">{t('nodes.node_details')}</h2>
    <Badge bg="primary">
      {Object.entries(nodeMapData?.node_details || {})
        .filter(([_, details]) => !details.address.includes('127.0.0.1')).length} {t('nodes.nodes')}
    </Badge>
  </Card.Header>
  <Card.Body>
    <Row xs={1} md={2} lg={4}>
      {Object.entries(nodeMapData?.node_details || {})
        .filter(([_, details]) => !details.address.includes('127.0.0.1'))
        .sort((a, b) => {
          // Sort by validator status first, then connectivity
          if (a[1].is_validator && !b[1].is_validator) return -1;
          if (!a[1].is_validator && b[1].is_validator) return 1;
          if (a[1].connected && !b[1].connected) return -1;
          if (!a[1].connected && b[1].connected) return 1;
          return a[0].localeCompare(b[0]);
        })
        .map(([nodeIdOrAddress, details]) => {
          const isNodeId = nodeIdOrAddress.startsWith('G');
          const address = details.address || nodeIdOrAddress;
          const quorumSet = isNodeId ? nodeMapData?.node_to_quorum_set[nodeIdOrAddress] : null;
          const connectionCount = quorumSet?.validators.length || 0;
          const isCoreTeam = isCoreTeamNode(address);
          
          return (
            <Col key={nodeIdOrAddress} className="mb-3">
              <Card className={`h-100 ${isCoreTeam ? "border-warning" : "border-primary"}`}>
                <Card.Header className={`${isCoreTeam ? "bg-warning bg-opacity-10" : ""} d-flex justify-content-between`}>
                  <div className="text-truncate" style={{ maxWidth: '80%' }}>
                    {isNodeId ? (
                      <>
                        <span className="fw-bold text-color-normal">{nodeIdOrAddress.substr(0, 6)}...{nodeIdOrAddress.substr(-6)}</span> {" "}
                        <CopyIcon textToCopy={nodeIdOrAddress} />
                      </>
                    ) : (
                      <span className="text-muted">Unknown Node ID</span>
                    )}
                  </div>
                  <div>
                    {details.is_validator && <Badge bg="primary" className="ms-1">V</Badge>}
                    {details.connected ? <Badge bg="success" className="ms-1">O</Badge> : <Badge bg="danger" className="ms-1">D</Badge>}
                  </div>
                </Card.Header>
                <Card.Body className="p-3">
                  <div className="mb-2">
                    <small className="text-muted d-block">{t('nodes.address')}:</small>
                    <div className="text-truncate small text-color-normal">
                      {address.split(':')[0]}
                      <span className="text-muted">:{address.split(':')[1]}</span>
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <small className="text-muted d-block">{t('nodes.type')}:</small>
                    <div>
                      {details.is_validator ? (
                        <Badge bg="primary">{t('nodes.validator')}</Badge>
                      ) : (
                        <Badge bg="secondary">{t('nodes.watcher')}</Badge>
                      )}
                      {isCoreTeam && <Badge bg="warning" className="ms-1">{t('nodes.core_team')}</Badge>}
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <small className="text-muted d-block">{t('nodes.location')}:</small>
                    <div className="small text-color-normal">
                      {details.geo?.city || "Unknown"}{details.geo?.city && details.geo?.country ? ", " : ""}{details.geo?.country || "Unknown"}
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <small className="text-muted d-block">{t('nodes.isp')}:</small>
                    <div className="small text-truncate text-color-normal">
                      {details.geo?.isp || details.geo?.org || "Unknown"}
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <small className="text-muted d-block">{t('nodes.version')}:</small>
                    <div className="small text-color-normal">
                      {details.version ? details.version.split('(')[0] : 'Unknown'}
                    </div>
                  </div>
                  
                  {isNodeId && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between">
                        <Badge bg="info">{t('nodes.trust')} {connectionCount}</Badge>
                        <Badge bg="success">{t('nodes.trusted_by')} {calculateTrustedByCounts()[nodeIdOrAddress] || 0}</Badge>
                      </div>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer className={`${details.connected ? 'bg-success' : 'bg-danger'} bg-opacity-10 p-2 text-center`}>
                  <small className={`${details.connected ? 'text-success' : 'text-danger'} fw-bold`}>
                    {details.connected ? t('nodes.status.connected') : t('nodes.status.disconnected')}
                  </small>
                </Card.Footer>
              </Card>
            </Col>
          );
        })}
    </Row>
  </Card.Body>
</Card>

      {/* Quorum Sets Table with non-empty filter */}
<Card className="mt-4">
  <Card.Header className="d-flex justify-content-between align-items-center">
    <h2 className="card-title">{t('nodes.active_quorum_sets')}</h2>
    <Badge bg="success">
      {Object.entries(nodeMapData?.quorum_set_status || {})
        .filter(([hash, isActive]) => {
          // Only count active quorums with at least one node or validator
          if (!isActive) return false;
          const nodes = nodeMapData?.hash_to_nodes[hash] || [];
          const validators = nodeMapData?.hash_to_validators[hash] || [];
          return nodes.length > 0 || validators.length > 0;
        }).length} {t('nodes.active')}
    </Badge>
  </Card.Header>
  <Card.Body className="p-0">
    <div className="table-responsive">
      <Table hover className="mb-0">
        <thead>
          <tr>
            <th>{t('nodes.quorum_hash')}</th>
            <th>{t('nodes.status')}</th>
            <th>{t('nodes.nodes')}</th>
            <th>{t('nodes.validators')}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(nodeMapData?.quorum_set_status || {})
            .filter(([hash, isActive]) => {
              // Only show active quorums with at least one node or validator
              if (!isActive) return false;
              const nodes = nodeMapData?.hash_to_nodes[hash] || [];
              const validators = nodeMapData?.hash_to_validators[hash] || [];
              return nodes.length > 0 || validators.length > 0;
            })
            .map(([hash, isActive]) => {
              const nodes = nodeMapData?.hash_to_nodes[hash] || [];
              const validators = nodeMapData?.hash_to_validators[hash] || [];
              
              return (
                <tr key={hash}>
                  <td>
                    <code className="small">{hash.substring(0, 16)}...</code>
                    <CopyIcon textToCopy={hash} />
                  </td>
                  <td>
                    <Badge bg="success">{t('nodes.active')}</Badge>
                  </td>
                  <td>
                    <Badge bg="info">{nodes.length}</Badge>
                  </td>
                  <td>
                    <Badge bg="primary">{validators.length}</Badge>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Table>
    </div>
  </Card.Body>
</Card>
    </div>
  );
};

export default Nodes;